import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { TimetableSlot } from '../types';

interface TimetableViewProps {
  slots: TimetableSlot[];
  onEditClick?: (() => void) | undefined;
  onSlotClick?: ((slot: TimetableSlot) => void) | undefined;
  editable?: boolean;
  onCellClick?: (day: string, time: string, slot: TimetableSlot | null) => void;
}

const TimetableView = ({ slots, onEditClick, onSlotClick, editable, onCellClick }: TimetableViewProps) => {
  const days = useMemo(
    () => [
      { id: 'Monday', label: 'Luni' },
      { id: 'Tuesday', label: 'Marți' },
      { id: 'Wednesday', label: 'Miercuri' },
      { id: 'Thursday', label: 'Joi' },
      { id: 'Friday', label: 'Vineri' },
    ],
    []
  );

  const timeSlots = useMemo(
    () => ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'],
    []
  );

  const slotMap = useMemo(() => {
    const map = new Map<string, TimetableSlot>();
    for (const s of slots || []) {
      map.set(`${s.day}|${s.startTime}`, s);
    }
    return map;
  }, [slots]);

  const getSlotForDayAndTime = (day: string, time: string) => slotMap.get(`${day}|${time}`) || null;

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lecture':
        return 'Curs';
      case 'lab':
        return 'Laborator';
      case 'tutorial':
        return 'Seminar';
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'lecture':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'lab':
        return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
      case 'tutorial':
        return 'bg-green-500/10 text-green-600 border-green-500/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const [isMobile, setIsMobile] = useState(false);
  const [activeDayId, setActiveDayId] = useState(days[0]?.id || 'Monday');

  useEffect(() => {
    if (!days.some((d) => d.id === activeDayId)) setActiveDayId(days[0]?.id || 'Monday');
  }, [activeDayId, days]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const apply = () => setIsMobile(Boolean(mq.matches));
    apply();
    try {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    } catch {
      window.addEventListener('resize', apply);
      return () => window.removeEventListener('resize', apply);
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-foreground">Orar săptămânal</h3>
            <p className="text-sm text-muted-foreground mt-1">Programul clasei și sălile alocate</p>
          </div>
          {onEditClick ? (
            <Button variant="default" size="default" onClick={onEditClick} iconName="Edit" iconPosition="left">
              Editează orarul
            </Button>
          ) : null}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isMobile ? (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm font-semibold text-foreground">Zi</div>
              <div className="text-xs text-muted-foreground">Pe mobil afișăm o zi (fără scroll orizontal).</div>
            </div>

            <div className="flex gap-2 overflow-x-auto overscroll-x-contain pb-1">
              {days.map((d) => {
                const active = d.id === activeDayId;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setActiveDayId(d.id)}
                    className={`shrink-0 px-3 py-1.5 rounded-md text-sm border transition-colors duration-150 ${
                      active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted/40'
                    }`}
                    aria-current={active ? 'true' : undefined}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              {timeSlots
                .filter((t) => editable || Boolean(getSlotForDayAndTime(activeDayId, t)))
                .map((time) => {
                  const slot = getSlotForDayAndTime(activeDayId, time);
                  if (slot) {
                    return (
                      <button
                        key={`${activeDayId}-${time}`}
                        type="button"
                        onClick={() => (onCellClick ? onCellClick(activeDayId, time, slot) : onSlotClick ? onSlotClick(slot) : null)}
                        className={`w-full p-3 rounded-md border text-left transition-all duration-150 hover:shadow-md ${getTypeColor(slot.type)}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-semibold">{slot.subject}</p>
                          <span className="text-xs opacity-75 whitespace-nowrap">{getTypeLabel(slot.type)}</span>
                        </div>
                        {slot.teacherName ? <div className="text-xs opacity-75 truncate">{slot.teacherName}</div> : null}
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs opacity-75">
                          <span className="inline-flex items-center gap-1">
                            <Icon name="Clock" size={12} />
                            <span>
                              {slot.startTime}–{slot.endTime}
                            </span>
                          </span>
                          {slot.room ? (
                            <span className="inline-flex items-center gap-1">
                              <Icon name="MapPin" size={12} />
                              <span>{slot.room}</span>
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  }

                  if (editable && onCellClick) {
                    return (
                      <button
                        key={`${activeDayId}-${time}-add`}
                        onClick={() => onCellClick(activeDayId, time, null)}
                        className="w-full rounded-md border border-dashed border-border bg-muted/20 hover:bg-muted/40 transition-colors duration-150 text-left px-3 py-2"
                        type="button"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground">{time}</span>
                          <span className="text-xs text-muted-foreground">Adaugă</span>
                        </div>
                      </button>
                    );
                  }

                  return null;
                })}

              {!editable && timeSlots.every((t) => !getSlotForDayAndTime(activeDayId, t)) ? (
                <div className="text-sm text-muted-foreground">Nu există lecții în această zi.</div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-6 border-b border-border">
                <div className="p-3 bg-muted/50 font-medium text-sm text-foreground">Ora</div>
                {days.map((day) => (
                  <div key={day.id} className="p-3 bg-muted/50 font-medium text-sm text-foreground text-center">
                    {day.label}
                  </div>
                ))}
              </div>

              {timeSlots.map((time) => (
                <div key={time} className="grid grid-cols-6 border-b border-border">
                  <div className="p-3 bg-muted/30 text-sm font-medium text-muted-foreground">{time}</div>
                  {days.map((day) => {
                    const slot = getSlotForDayAndTime(day.id, time);
                    return (
                      <div key={`${day.id}-${time}`} className="p-2">
                        {slot ? (
                          <button
                            onClick={() => (onCellClick ? onCellClick(day.id, time, slot) : onSlotClick ? onSlotClick(slot) : null)}
                            className={`w-full p-3 rounded-md border text-left transition-all duration-150 hover:shadow-md ${getTypeColor(slot.type)}`}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-xs font-semibold">{slot.subject}</p>
                              <span className="text-xs opacity-75">{getTypeLabel(slot.type)}</span>
                            </div>
                            {slot.teacherName ? <div className="text-[11px] opacity-75 truncate">{slot.teacherName}</div> : null}
                            <div className="flex items-center gap-1 text-xs opacity-75">
                              <Icon name="MapPin" size={10} />
                              <span>{slot.room}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs opacity-75 mt-1">
                              <Icon name="Clock" size={10} />
                              <span>
                                {slot.startTime} - {slot.endTime}
                              </span>
                            </div>
                          </button>
                        ) : editable && onCellClick ? (
                          <button
                            onClick={() => onCellClick(day.id, time, null)}
                            className="w-full h-full min-h-[80px] rounded-md border border-dashed border-border bg-muted/20 hover:bg-muted/40 transition-colors duration-150 text-left px-3 py-2"
                            type="button"
                          >
                            <span className="text-xs text-muted-foreground">Adaugă</span>
                          </button>
                        ) : (
                          <div className="w-full h-full min-h-[80px] rounded-md border border-dashed border-border bg-muted/20" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Legendă orar</p>
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500/30" />
                <span className="text-muted-foreground">Curs</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-purple-500/20 border border-purple-500/30" />
                <span className="text-muted-foreground">Laborator</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/30" />
                <span className="text-muted-foreground">Seminar</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableView;
